import { create } from 'zustand';
import { Template } from './types';
import { templatesApi } from '../../../shared/api/templates.api';

interface TemplateState {
  templates: Template[];
  isLoading: boolean;
  error: string | null;
  
  // API actions
  fetchTemplates: () => Promise<void>;
  createTemplate: (data: Omit<Template, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Template>;
  updateTemplate: (id: string, updates: Partial<Omit<Template, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<Template>;
  deleteTemplate: (id: string) => Promise<void>;
  
  // Local actions
  setTemplates: (templates: Template[]) => void;
  addTemplate: (template: Template) => void;
  updateLocalTemplate: (id: string, updates: Partial<Template>) => void;
  deleteLocalTemplate: (id: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  getTemplateById: (id: string) => Template | undefined;
}

export const useTemplateStore = create<TemplateState>((set, get) => ({
  templates: [],
  isLoading: false,
  error: null,
  
  // API actions
  fetchTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      const templates = await templatesApi.getAll();
      set({ templates, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch templates';
      set({ error: errorMessage, isLoading: false });
      console.error('Error fetching templates:', error);
      throw error;
    }
  },
  
  createTemplate: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const template = await templatesApi.create(data);
      set((state) => ({ 
        templates: [...state.templates, template],
        isLoading: false,
      }));
      return template;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create template';
      set({ error: errorMessage, isLoading: false });
      console.error('Error creating template:', error);
      throw error;
    }
  },
  
  updateTemplate: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const template = await templatesApi.update(id, updates);
      set((state) => ({
        templates: state.templates.map((t) => t.id === id ? template : t),
        isLoading: false,
      }));
      return template;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update template';
      set({ error: errorMessage, isLoading: false });
      console.error('Error updating template:', error);
      throw error;
    }
  },
  
  deleteTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await templatesApi.delete(id);
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        isLoading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete template';
      set({ error: errorMessage, isLoading: false });
      console.error('Error deleting template:', error);
      throw error;
    }
  },
  
  // Local actions
  setTemplates: (templates) => set({ templates }),
  
  addTemplate: (template) => 
    set((state) => ({ 
      templates: [...state.templates, template] 
    })),
  
  updateLocalTemplate: (id, updates) =>
    set((state) => ({
      templates: state.templates.map((tpl) =>
        tpl.id === id ? { ...tpl, ...updates, updatedAt: new Date() } : tpl
      ),
    })),
  
  deleteLocalTemplate: (id) =>
    set((state) => ({
      templates: state.templates.filter((tpl) => tpl.id !== id),
    })),
  
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  getTemplateById: (id) => get().templates.find((t) => t.id === id),
}));